/**
 * Scoring Methodology System
 * Provides standardized, reliable scoring calculations for all vendor comparison metrics
 */

/**
 * SCORING SCALE: 0-100
 * 95-100: Excellent (Best in class)
 * 85-94:  Good (Above average)
 * 70-84:  Fair (Meets expectations)
 * 50-69:  Poor (Below expectations)
 * 0-49:   Very Poor (Significantly lacking)
 */

// ============================================
// REVIEWS SCORING
// ============================================
export const calculateReviewScore = (rating, reviewCount = 0, allReviewCounts = []) => {
  if (!rating || rating === 0) return 0;
  
  // Base score from star rating (0-5 scale)
  let baseScore = (rating / 5) * 100;
  
  // Confidence modifier based on review count
  // Only apply penalty if this vendor has notably fewer reviews than others.
  // If all vendors have similarly low review counts, don't penalize any of them.
  let confidenceModifier = 1.0;
  const maxReviewCount = allReviewCounts?.length > 0 ? Math.max(...allReviewCounts) : reviewCount;
  
  // Only apply confidence penalty if this vendor has significantly fewer reviews
  // than the best-reviewed option (at least 5x fewer). This prevents penalizing
  // all vendors equally when none have many reviews.
  if (maxReviewCount > 0 && reviewCount < maxReviewCount / 5) {
    if (reviewCount < 10) confidenceModifier = 0.85;
    else if (reviewCount < 50) confidenceModifier = 0.90;
    else if (reviewCount < 100) confidenceModifier = 0.95;
    else confidenceModifier = 0.98;
  }
  
  return Math.round(baseScore * confidenceModifier);
};

// ============================================
// PRICE SCORING
// ============================================
export const calculatePriceScore = (vendorPrice, allPrices = [], userBudget = null) => {
  if (!vendorPrice || allPrices?.length === 0) return 0;
  
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const priceRange = maxPrice - minPrice;
  
  // If all prices are the same
  if (priceRange === 0) return 100;
  
  // If a budget is set and ALL vendors are within budget, score relative to each other
  // but floor at 70 (all meet the requirement, just some are better value)
  const allWithinBudget = userBudget && allPrices?.every(p => p <= userBudget);
  
  // Calculate score: lower price = higher score
  // Best price (min) = 100, worst price (max) = 50
  const normalizedPosition = (vendorPrice - minPrice) / priceRange;
  let score = 100 - (normalizedPosition * 50);
  
  // If all options are within budget, the worst-priced still meets the requirement.
  // Floor the score at 70 so no option is penalized below "good" just for being pricier
  // when all options satisfy the budget constraint.
  if (allWithinBudget) {
    score = Math.max(70, score);
  }
  
  return Math.round(score);
};

// ============================================
// PROCESSOR/CHIP SCORING
// ============================================
export const calculateProcessorScore = (processorSpec, requiredSpec = null) => {
  if (!processorSpec) return 0;
  
  const spec = processorSpec?.toLowerCase();
  
  let score = 0;
  
  // Intel processors
  if (spec?.includes('i9') || spec?.includes('ultra 9')) score = 100;
  else if (spec?.includes('i7') || spec?.includes('ultra 7')) score = 95;
  else if (spec?.includes('i5') || spec?.includes('ultra 5')) score = 85;
  else if (spec?.includes('i3') || spec?.includes('ultra 3')) score = 70;
  
  // AMD processors
  else if (spec?.includes('ryzen 9') || spec?.includes('threadripper')) score = 100;
  else if (spec?.includes('ryzen 7')) score = 95;
  else if (spec?.includes('ryzen 5')) score = 85;
  else if (spec?.includes('ryzen 3')) score = 70;
  
  // Apple Silicon
  else if (spec?.includes('m4')) score = 100;
  else if (spec?.includes('m3 pro') || spec?.includes('m3 max')) score = 98;
  else if (spec?.includes('m3')) score = 90;
  else if (spec?.includes('m2 pro') || spec?.includes('m2 max')) score = 95;
  else if (spec?.includes('m2')) score = 85;
  else if (spec?.includes('m1 pro') || spec?.includes('m1 max')) score = 90;
  else if (spec?.includes('m1')) score = 80;
  
  // Core count based scoring (if specific model not identified)
  else {
    const coreMatch = spec?.match(/(\d+)[-\s]core/);
    if (coreMatch) {
      const cores = parseInt(coreMatch?.[1]);
      if (cores >= 16) score = 100;
      else if (cores >= 12) score = 95;
      else if (cores >= 10) score = 90;
      else if (cores >= 8) score = 85;
      else if (cores >= 6) score = 75;
      else if (cores >= 4) score = 65;
      else score = 50;
    } else {
      score = 60; // Default for unrecognized processors
    }
  }
  
  // If a requirement was specified and this vendor meets/matches it, award 100
  if (requiredSpec) {
    const req = String(requiredSpec)?.toLowerCase();
    if (spec?.includes(req) || req?.includes(spec)) return 100;
  }
  
  return score;
};

// ============================================
// MEMORY (RAM) SCORING
// ============================================
export const calculateMemoryScore = (memoryGB, requiredGB = null) => {
  if (!memoryGB) return 0;
  
  const memory = parseInt(memoryGB);
  
  // If vendor meets or exceeds the requirement, score 100
  if (requiredGB != null) {
    const req = parseInt(requiredGB);
    if (!isNaN(req) && req > 0 && memory >= req) return 100;
  }
  
  if (memory >= 64) return 100;
  if (memory >= 32) return 95;
  if (memory >= 24) return 90;
  if (memory >= 16) return 85;
  if (memory >= 12) return 75;
  if (memory >= 8) return 65;
  if (memory >= 4) return 50;
  
  return 30;
};

// ============================================
// STORAGE SCORING
// ============================================
export const calculateStorageScore = (storageGB, isSSD = true, requiredGB = null) => {
  if (!storageGB) return 0;
  
  const storage = parseInt(storageGB);
  
  // If vendor meets or exceeds the requirement, score 100
  if (requiredGB != null) {
    const req = parseInt(requiredGB);
    if (!isNaN(req) && req > 0 && storage >= req) return 100;
  }
  
  let baseScore = 0;
  
  if (storage >= 2000) baseScore = 100;
  else if (storage >= 1000) baseScore = 95;
  else if (storage >= 512) baseScore = 85;
  else if (storage >= 256) baseScore = 70;
  else if (storage >= 128) baseScore = 55;
  else baseScore = 40;
  
  // SSD bonus: +10 points (capped at 100)
  // HDD penalty: -15 points
  if (isSSD) {
    baseScore = Math.min(100, baseScore + 10);
  } else {
    baseScore = Math.max(0, baseScore - 15);
  }
  
  return baseScore;
};

// ============================================
// DELIVERY TIME SCORING
// ============================================
export const calculateDeliveryScore = (deliveryDays, requiredDays = null) => {
  if (deliveryDays === null || deliveryDays === undefined) return 0;
  
  let days = parseInt(deliveryDays);
  
  // If vendor meets or beats the requirement, score 100
  if (requiredDays != null) {
    const req = parseInt(requiredDays);
    if (!isNaN(req) && req > 0 && days <= req) return 100;
  }
  
  if (days === 0) return 100; // Same day
  if (days <= 1) return 98;   // Next day
  if (days <= 2) return 95;   // 2 days
  if (days <= 3) return 90;   // 3 days
  if (days <= 5) return 85;   // 5 days
  if (days <= 7) return 75;   // 1 week
  if (days <= 14) return 65;  // 2 weeks
  if (days <= 30) return 50;  // 1 month
  
  return 30; // Over 1 month
};

// ============================================
// WARRANTY SCORING
// ============================================
export const calculateWarrantyScore = (warrantyYears, hasExtendedOptions = false, requiredYears = null) => {
  if (!warrantyYears) return 0;
  
  let years = parseFloat(warrantyYears);
  
  // If vendor meets or exceeds the requirement, score 100
  if (requiredYears != null) {
    const req = parseFloat(requiredYears);
    if (!isNaN(req) && req > 0 && years >= req) return 100;
  }
  
  let score = 0;
  
  if (years >= 5) score = 100;
  else if (years >= 3) score = 95;
  else if (years >= 2) score = 85;
  else if (years >= 1) score = 75;
  else if (years >= 0.5) score = 60; // 6 months
  else score = 40;
  
  // Extended options bonus
  if (hasExtendedOptions && score < 95) {
    score = Math.min(100, score + 10);
  }
  
  return score;
};

// ============================================
// INTEREST RATE SCORING (for loans/mortgages)
// ============================================
export const calculateInterestRateScore = (vendorRate, allRates = [], maxAcceptableRate = null) => {
  if (!vendorRate || allRates?.length === 0) return 0;
  
  const minRate = Math.min(...allRates);
  const maxRate = Math.max(...allRates);
  const rateRange = maxRate - minRate;
  
  // If all rates are the same
  if (rateRange === 0) return 100;
  
  // If a max acceptable rate is set and ALL vendors are within it,
  // floor the score at 70 so no vendor is penalized below "good"
  const allWithinLimit = maxAcceptableRate && allRates?.every(r => r <= maxAcceptableRate);
  
  // Calculate score: lower rate = higher score
  // Best rate (min) = 100, worst rate (max) = 50
  const normalizedPosition = (vendorRate - minRate) / rateRange;
  let score = 100 - (normalizedPosition * 50);
  
  if (allWithinLimit) {
    score = Math.max(70, score);
  }
  
  return Math.round(score);
};

// ============================================
// FUEL EFFICIENCY SCORING (MPG)
// ============================================
export const calculateFuelEfficiencyScore = (mpg, requiredMpg = null) => {
  if (!mpg) return 0;
  
  const efficiency = parseInt(mpg);
  
  // If vendor meets or exceeds the requirement, score 100
  if (requiredMpg != null) {
    const req = parseInt(requiredMpg);
    if (!isNaN(req) && req > 0 && efficiency >= req) return 100;
  }
  
  if (efficiency >= 50) return 100; // Hybrid/Electric level
  if (efficiency >= 40) return 95;
  if (efficiency >= 35) return 90;
  if (efficiency >= 30) return 85;
  if (efficiency >= 25) return 75;
  if (efficiency >= 20) return 65;
  if (efficiency >= 15) return 50;
  
  return 30;
};

// ============================================
// SAFETY RATING SCORING
// ============================================
export const calculateSafetyScore = (safetyRating) => {
  if (!safetyRating) return 0;
  
  // NHTSA 5-star scale or IIHS ratings
  const rating = safetyRating?.toString()?.toLowerCase();
  
  if (rating?.includes('5') || rating?.includes('top safety pick+')) return 100;
  if (rating?.includes('4') || rating?.includes('top safety pick')) return 85;
  if (rating?.includes('3') || rating?.includes('good')) return 70;
  if (rating?.includes('2') || rating?.includes('acceptable')) return 55;
  
  return 40;
};

// ============================================
// SCREEN SIZE SCORING (for displays)
// ============================================
export const calculateScreenSizeScore = (inches, targetSize = null) => {
  if (!inches) return 0;
  
  const size = parseFloat(inches);
  
  // If user specified target size, score based on proximity
  if (targetSize) {
    const difference = Math.abs(size - targetSize);
    if (difference === 0) return 100;
    if (difference <= 2) return 95;
    if (difference <= 4) return 85;
    if (difference <= 6) return 70;
    return 50;
  }
  
  // General scoring (larger is better for most use cases)
  if (size >= 65) return 100;
  if (size >= 55) return 95;
  if (size >= 50) return 90;
  if (size >= 43) return 85;
  if (size >= 32) return 75;
  if (size >= 24) return 65;
  
  return 50;
};

// ============================================
// RESOLUTION SCORING
// ============================================
export const calculateResolutionScore = (resolution) => {
  if (!resolution) return 0;
  
  const res = resolution?.toLowerCase();
  
  if (res?.includes('8k')) return 100;
  if (res?.includes('4k') || res?.includes('uhd') || res?.includes('2160')) return 95;
  if (res?.includes('1440') || res?.includes('qhd')) return 85;
  if (res?.includes('1080') || res?.includes('fhd')) return 75;
  if (res?.includes('720') || res?.includes('hd')) return 60;
  
  return 40;
};

// ============================================
// BATTERY LIFE SCORING (hours)
// ============================================
export const calculateBatteryScore = (hours, requiredHours = null) => {
  if (!hours) return 0;
  
  const batteryLife = parseFloat(hours);
  
  // If vendor meets or exceeds the requirement, score 100
  if (requiredHours != null) {
    const req = parseFloat(requiredHours);
    if (!isNaN(req) && req > 0 && batteryLife >= req) return 100;
  }
  
  if (batteryLife >= 20) return 100;
  if (batteryLife >= 15) return 95;
  if (batteryLife >= 12) return 90;
  if (batteryLife >= 10) return 85;
  if (batteryLife >= 8) return 75;
  if (batteryLife >= 6) return 65;
  if (batteryLife >= 4) return 50;
  
  return 30;
};

// ============================================
// CAMERA QUALITY SCORING (megapixels)
// ============================================
export const calculateCameraScore = (megapixels, hasOpticalZoom = false, hasNightMode = false, requiredMp = null) => {
  if (!megapixels) return 0;
  
  const mp = parseInt(megapixels);
  
  // If vendor meets or exceeds the requirement, score 100
  if (requiredMp != null) {
    const req = parseInt(requiredMp);
    if (!isNaN(req) && req > 0 && mp >= req) return 100;
  }
  
  let score = 0;
  
  if (mp >= 108) score = 100;
  else if (mp >= 64) score = 95;
  else if (mp >= 48) score = 90;
  else if (mp >= 24) score = 85;
  else if (mp >= 12) score = 75;
  else if (mp >= 8) score = 65;
  else score = 50;
  
  // Feature bonuses
  if (hasOpticalZoom && score < 95) score = Math.min(100, score + 5);
  if (hasNightMode && score < 95) score = Math.min(100, score + 5);
  
  return score;
};

// ============================================
// GENERIC PERCENTAGE SCORING
// ============================================
export const calculatePercentageScore = (value, isHigherBetter = true, allValues = [], requirement = null) => {
  if (value === null || value === undefined) return 0;
  
  // If we have comparison values, use relative scoring
  if (allValues?.length > 0) {
    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);
    const range = maxVal - minVal;
    
    if (range === 0) return 100;
    
    // If a requirement exists and all values meet it, floor at 70
    const reqNum = requirement != null ? parseFloat(String(requirement)?.replace(/[^0-9.]/g, '')) : NaN;
    const allMeetReq = !isNaN(reqNum) && reqNum > 0 && allValues?.every(v =>
      isHigherBetter ? v >= reqNum : v <= reqNum
    );
    
    const normalizedPosition = (value - minVal) / range;
    
    let score;
    if (isHigherBetter) {
      score = Math.round(50 + (normalizedPosition * 50));
    } else {
      score = Math.round(100 - (normalizedPosition * 50));
    }
    
    if (allMeetReq) {
      score = Math.max(70, score);
    }
    
    return score;
  }
  
  // Absolute scoring if no comparison values
  return Math.min(100, Math.max(0, Math.round(value)));
};

// ============================================
// SERVICE QUOTE FIELD SCORING
// ============================================

/**
 * Score a total cost / labor / material cost field.
 * Lower cost relative to other vendors = higher score (same as price scoring).
 */
export const calculateServiceCostScore = (vendorCost, allCosts = [], userBudget = null) => {
  if (!vendorCost || allCosts?.length === 0) return 0;
  const cost = parseFloat(String(vendorCost)?.replace(/[^0-9.]/g, ''));
  if (isNaN(cost) || cost <= 0) return 0;
  const validCosts = allCosts
    ?.map(c => parseFloat(String(c)?.replace(/[^0-9.]/g, '')))
    ?.filter(c => !isNaN(c) && c > 0);
  if (validCosts?.length === 0) return 70;
  const minCost = Math.min(...validCosts);
  const maxCost = Math.max(...validCosts);
  if (maxCost === minCost) return 100;
  
  const normalizedPosition = (cost - minCost) / (maxCost - minCost);
  let score = Math.round(100 - normalizedPosition * 50);
  
  // If a budget is set and all vendors are within it, floor at 70
  const allWithinBudget = userBudget && validCosts?.every(c => c <= userBudget);
  if (allWithinBudget) {
    score = Math.max(70, score);
  }
  
  return score;
};

/**
 * Score a timeline / completion time field.
 * Parses strings like "2 weeks", "10 days", "3 months", "1-2 weeks".
 * Shorter timeline = higher score.
 */
export const calculateTimelineScore = (timelineText, requiredTimeline = null) => {
  if (!timelineText) return 0;
  const text = String(timelineText)?.toLowerCase()?.trim();

  // Extract the first number
  const numMatch = text?.match(/(\d+(?:\.\d+)?)/);
  if (!numMatch) return 60; // Text present but not parseable — neutral

  const num = parseFloat(numMatch?.[1]);

  // Convert everything to days
  let days = num;
  if (text?.includes('month')) days = num * 30;
  else if (text?.includes('week')) days = num * 7;
  else if (text?.includes('year')) days = num * 365;
  // else assume days

  // If vendor meets or beats the required timeline, score 100
  if (requiredTimeline) {
    const reqText = String(requiredTimeline)?.toLowerCase()?.trim();
    const reqMatch = reqText?.match(/(\d+(?:\.\d+)?)/);
    if (reqMatch) {
      const reqNum = parseFloat(reqMatch?.[1]);
      let reqDays = reqNum;
      if (reqText?.includes('month')) reqDays = reqNum * 30;
      else if (reqText?.includes('week')) reqDays = reqNum * 7;
      else if (reqText?.includes('year')) reqDays = reqNum * 365;
      if (days <= reqDays) return 100;
    }
  }

  if (days <= 1) return 100;
  if (days <= 3) return 97;
  if (days <= 7) return 93;
  if (days <= 14) return 88;
  if (days <= 21) return 82;
  if (days <= 30) return 75;
  if (days <= 60) return 65;
  if (days <= 90) return 55;
  if (days <= 180) return 45;
  return 35;
};

/**
 * Score a warranty / guarantee field.
 * Parses strings like "1 year", "2 years", "lifetime", "6 months".
 */
export const calculateServiceWarrantyScore = (warrantyText, requiredWarranty = null) => {
  if (!warrantyText) return 0;
  const text = String(warrantyText)?.toLowerCase()?.trim();

  if (text?.includes('lifetime') || text?.includes('unlimited')) return 100;

  const numMatch = text?.match(/(\d+(?:\.\d+)?)/);
  if (!numMatch) {
    // Has text but no number — data present, neutral score
    if (text?.length > 2) return 65;
    return 0;
  }

  const num = parseFloat(numMatch?.[1]);
  let years = num;
  if (text?.includes('month')) years = num / 12;
  else if (text?.includes('day')) years = num / 365;
  // else assume years

  return calculateWarrantyScore(years, false, requiredWarranty != null ? parseFloat(String(requiredWarranty)?.replace(/[^0-9.]/g, '')) || null : null);
};

/**
 * Score experience / years in business.
 * More years = higher score.
 */
export const calculateExperienceScore = (experienceText, requiredYears = null) => {
  if (!experienceText) return 0;
  const text = String(experienceText)?.toLowerCase();
  const numMatch = text?.match(/(\d+)/);
  if (!numMatch) return 60;
  let years = parseInt(numMatch?.[1]);
  
  // If vendor meets or exceeds the requirement, score 100
  if (requiredYears != null) {
    const req = parseInt(String(requiredYears)?.replace(/[^0-9]/g, ''));
    if (!isNaN(req) && req > 0 && years >= req) return 100;
  }
  
  if (years >= 20) return 100;
  if (years >= 15) return 95;
  if (years >= 10) return 90;
  if (years >= 7) return 85;
  if (years >= 5) return 78;
  if (years >= 3) return 70;
  if (years >= 1) return 60;
  return 50;
};

/**
 * Score licensing / certifications.
 * Presence and detail of licensing info = higher score.
 */
export const calculateLicensingScore = (licensingText) => {
  if (!licensingText) return 0;
  const text = String(licensingText)?.toLowerCase()?.trim();
  if (!text || text?.length < 3) return 0;

  let score = 70; // Base: has some licensing info (raised from 60 — presence of data is meaningful)
  if (text?.includes('licensed') || text?.includes('certified') || text?.includes('insured')) score += 15;
  if (text?.includes('bonded')) score += 10;
  if (text?.includes('state') || text?.includes('contractor')) score += 5;
  if (text?.length > 50) score += 5; // Detailed response

  return Math.min(100, score);
};

/**
 * Score availability / response time.
 * Immediate / same-week = higher score.
 */
export const calculateAvailabilityScore = (availabilityText) => {
  if (!availabilityText) return 0;
  const text = String(availabilityText)?.toLowerCase()?.trim();
  if (!text) return 0;

  if (text?.includes('immediate') || text?.includes('same day') || text?.includes('today')) return 100;
  if (text?.includes('next day') || text?.includes('tomorrow')) return 95;
  if (text?.includes('this week') || text?.includes('2-3 day') || text?.includes('2 day') || text?.includes('3 day')) return 88;
  if (text?.includes('next week') || text?.includes('1 week') || text?.includes('7 day')) return 78;
  if (text?.includes('2 week') || text?.includes('two week')) return 68;
  if (text?.includes('month')) return 50;

  // Has text but no recognized pattern — neutral (raised from 65 to 75: data present means vendor responded)
  return 75;
};

// ============================================
// MASTER SCORING FUNCTION (updated)
// ============================================
export const calculateMetricScore = (metricId, value, options = {}) => {
  if (!value && value !== 0) return 0;

  switch (metricId) {
    case 'reviews':
      return calculateReviewScore(value, options?.reviewCount, options?.allReviewCounts || []);

    case 'price': case 'budget': case 'cost': case 'priceMax':
      return calculatePriceScore(value, options?.allPrices || [], options?.userBudget || null);

    // ── Service cost fields ───────────────────────────────────────────────
    case 'totalCost': case 'laborCost': case 'materialCost': case 'totalAmount':
      return calculateServiceCostScore(value, options?.allCosts || options?.allPrices || [], options?.userBudget || null);

    // ── Service timeline fields ───────────────────────────────────────────
    case 'timeline': case 'completionTimeline': case 'projectTimeline': case 'startDate':
      return calculateTimelineScore(value, options?.requirement || null);

    // ── Service warranty fields ───────────────────────────────────────────
    case 'warranty': case 'warrantySupport': case 'guarantee':
      return calculateServiceWarrantyScore(value, options?.requirement || null);

    // ── Service quality / vendor fields ──────────────────────────────────
    case 'experience': case 'yearsExperience':
      return calculateExperienceScore(value, options?.requirement || null);

    case 'licensing': case 'certifications': case 'licenseNumber':
      return calculateLicensingScore(value);

    case 'availability': case 'responseTime':
      return calculateAvailabilityScore(value);

    // ── Product spec fields ───────────────────────────────────────────────
    case 'processor': case'chip': case'cpu':
      return calculateProcessorScore(value, options?.requirement || null);

    case 'memory': case'ram':
      return calculateMemoryScore(value, options?.requirement || null);

    case 'storage': case'storageCapacity':
      return calculateStorageScore(value, options?.isSSD, options?.requirement || null);

    case 'delivery': case'deliveryTime': case'shipping':
      return calculateDeliveryScore(value, options?.requirement || null);

    case 'interestRate': case'rate': case'apr':
      return calculateInterestRateScore(value, options?.allRates || [], options?.requirement || null);

    case 'fuelEfficiency': case'mpg':
      return calculateFuelEfficiencyScore(value, options?.requirement || null);

    case 'safety': case'safetyFeatures': case'safetyRating':
      return calculateSafetyScore(value);

    case 'screenSize': case'displaySize':
      return calculateScreenSizeScore(value, options?.targetSize || options?.requirement || null);

    case 'resolution': case'displayResolution':
      return calculateResolutionScore(value);

    case 'batteryLife':
    case 'battery':
      return calculateBatteryScore(value, options?.requirement || null);

    case 'camera': case'cameraQuality':
      return calculateCameraScore(value, options?.hasOpticalZoom, options?.hasNightMode, options?.requirement || null);

    default:
      // Generic scoring for unrecognized metrics
      return calculatePercentageScore(value, options?.isHigherBetter, options?.allValues || [], options?.requirement || null);
  }
};

// ============================================
// SCORE STATUS HELPER
// ============================================
export const getScoreStatus = (score) => {
  if (score >= 95) return 'excellent';
  if (score >= 85) return 'good';
  if (score >= 70) return 'fair';
  if (score >= 50) return 'poor';
  return 'very-poor';
};

export default {
  calculateMetricScore,
  getScoreStatus,
  calculateReviewScore,
  calculatePriceScore,
  calculateProcessorScore,
  calculateMemoryScore,
  calculateStorageScore,
  calculateDeliveryScore,
  calculateWarrantyScore,
  calculateInterestRateScore,
  calculateFuelEfficiencyScore,
  calculateSafetyScore,
  calculateScreenSizeScore,
  calculateResolutionScore,
  calculateBatteryScore,
  calculateCameraScore,
  calculatePercentageScore,
  calculateTextSimilarityScore
};

/**
 * Text similarity scoring: compares a vendor's text value against the project requirement.
 * Returns 0-100 based on how well the option matches what the user asked for.
 */
export function calculateTextSimilarityScore(vendorValue, projectRequirement) {
  if (!vendorValue || !projectRequirement) return 0;

  const vendor = String(vendorValue)?.toLowerCase()?.trim();
  const required = String(projectRequirement)?.toLowerCase()?.trim();

  if (!vendor || !required) return 0;

  // Exact match
  if (vendor === required) return 100;

  // Contains match (one includes the other)
  if (vendor?.includes(required) || required?.includes(vendor)) return 95;

  // Word-level Jaccard similarity
  const vendorWords = new Set(vendor?.split(/[\s,/-]+/)?.filter(w => w?.length > 1));
  const requiredWords = new Set(required?.split(/[\s,/-]+/)?.filter(w => w?.length > 1));

  if (vendorWords?.size === 0 || requiredWords?.size === 0) return 35;

  const intersection = [...vendorWords]?.filter(w => requiredWords?.has(w))?.length;
  const union = new Set([...vendorWords, ...requiredWords])?.size;
  const jaccard = union > 0 ? intersection / union : 0;

  // Character bigram Dice coefficient for partial word matches
  const getBigrams = (str) => {
    const bigrams = new Set();
    for (let i = 0; i < str?.length - 1; i++) {
      bigrams?.add(str?.slice(i, i + 2));
    }
    return bigrams;
  };
  const vBigrams = getBigrams(vendor);
  const rBigrams = getBigrams(required);
  const bigramIntersection = [...vBigrams]?.filter(b => rBigrams?.has(b))?.length;
  const dice = (vBigrams?.size + rBigrams?.size) > 0
    ? (2 * bigramIntersection) / (vBigrams?.size + rBigrams?.size)
    : 0;

  // Combine Jaccard + Dice for final similarity
  const similarity = (jaccard * 0.6) + (dice * 0.4);

  if (similarity >= 0.8) return Math.round(85 + similarity * 15);
  if (similarity >= 0.5) return Math.round(50 + similarity * 70);
  if (similarity >= 0.2) return Math.round(35 + similarity * 75);

  return 35; // Low match floor — data present but doesn't match requirement
}